import { useQuery } from '@tanstack/react-query';
import { GlobalReactQueryOptions } from '../../config';
import { AgeCategory } from '../../../models/AgeCategory';
import {
    fetchLibxProductsFromCatalogue,
    fetchProductsFromCatalogue,
    FetchProductsFromCatalogueResponse,
    fetchProductsFromLanguageLearning,
    fetchProductsFromLote
} from '../../api/productList/fetchProductsFromCatalogue';
import { ProductFilter, ProductSorting } from '../../../models/Product';
import { LoanFormat } from '../../../models/LoanFormat';
import { UseQueryResult } from '../../api/query-types';
import { browserClient } from '../../react-query';

type UseProductsFromCatalogueQuery = UseQueryResult<'data', FetchProductsFromCatalogueResponse> & {
    queryKey: Array<any>;
};

type UseProductsFromCatalogueOptions = {
    readonly fetchFunc?: typeof fetchProductsFromCatalogue;
    readonly ageCategory?: AgeCategory;
    readonly page?: number;
    readonly pageSize?: number;
    readonly sortBy?: ProductSorting;
    language?: string | undefined;
    shopLanguage: string | undefined;
};

const emptyProductsFromCatalogue = {
    total: 0,
    products: []
};

/**
 * Get products for a specific loan format from the catalogue. It is possible to narrow the products by passing a
 * filter and pagination options.
 * Useful to populate product groups for example.
 */
export const useProductsFromCatalogueQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    {
        fetchFunc = fetchProductsFromCatalogue,
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    }: UseProductsFromCatalogueOptions = { shopLanguage: undefined }
): UseProductsFromCatalogueQuery => {
    const params = {
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    };
    const queryKey = ['productsFromCatalogue', loanFormat, filterMap, params];
    const { data, ...result } = useQuery(queryKey, () => fetchFunc(loanFormat, filterMap, params), {
        ...GlobalReactQueryOptions
    });

    return {
        ...result,
        data: data ?? emptyProductsFromCatalogue,
        queryKey
    };
};

export function invalidateProductsFromCatalogue(loanFormat: LoanFormat) {
    return browserClient.invalidateQueries(['productsFromCatalogue', loanFormat]);
}

/**
 * Get products for a specific loan format from lote. It is possible to narrow the products by passing a
 * filter and pagination options.
 * Useful to populate product groups for example.
 */
export const useProductsFromLoteQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    {
        fetchFunc = fetchProductsFromLote,
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    }: UseProductsFromCatalogueOptions = { shopLanguage: undefined }
): UseProductsFromCatalogueQuery => {
    const params = {
        ageCategory,
        page,
        pageSize,
        sortBy
    };

    const queryKey = ['productsFromLote', loanFormat, filterMap, params];
    const { data, ...result } = useQuery(queryKey, () => fetchFunc(loanFormat, filterMap, params), {
        ...GlobalReactQueryOptions
    });
    return {
        ...result,
        data: data ?? emptyProductsFromCatalogue,
        queryKey
    };
};

export function invalidateProductsFromLote(loanFormat: LoanFormat) {
    return browserClient.invalidateQueries(['productsFromLote', loanFormat]);
}

/**
 * Get products for a specific loan format from lote. It is possible to narrow the products by passing a
 * filter and pagination options.
 * Useful to populate product groups for example.
 */
export const useProductsFromLanguageLearningQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    {
        fetchFunc = fetchProductsFromLanguageLearning,
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    }: UseProductsFromCatalogueOptions = { shopLanguage: undefined }
): UseProductsFromCatalogueQuery => {
    const params = {
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    };
    const queryKey = ['productsFromLanguageLearning', loanFormat, filterMap, params];
    const { data, ...result } = useQuery(queryKey, () => fetchFunc(loanFormat, filterMap, params), {
        ...GlobalReactQueryOptions
    });

    return {
        ...result,
        data: data ?? emptyProductsFromCatalogue,
        queryKey
    };
};

export function invalidateProductsFromLanguageLearning(loanFormat: LoanFormat) {
    return browserClient.invalidateQueries(['productsFromLanguageLearning', loanFormat]);
}

export const useLibxProductsFromCatalogueQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    options: UseProductsFromCatalogueOptions = { shopLanguage: undefined }
): UseProductsFromCatalogueQuery => {
    return useProductsFromCatalogueQuery(loanFormat, filterMap, {
        ...options,
        fetchFunc: fetchLibxProductsFromCatalogue
    });
};
